import React, { useEffect, useRef, useState } from 'react';

const STEPS = [
    "Gathering selected content",
    "Assembling resources",
    "Generating lesson summary",
    "Matching syllabus codes",
    "Checking content coverage",
    "Ordering lesson flow",
    "Finalising your plan",
];

const TILE_COLORS = ['secondary', 'primary'];
const TILE_COUNT = 20;
const MIN_STEPS = 3;
const CHAR_DELAY_MIN = 28;
const CHAR_DELAY_RANGE = 20;
const STEP_HOLD_MS = 900;
const FINISH_HOLD_MS = 800;
const FADE_MS = 400;

const LessonPlanGenerating = ({ isComplete, onDone }) => {
    const [blocks, setBlocks] = useState(() =>
        Array.from({ length: TILE_COUNT }, () => ({ visible: false, color: null }))
    );
    const [prevStep, setPrevStep] = useState(null);
    const [currentTyped, setCurrentTyped] = useState('');
    const [phase, setPhase] = useState('starting');
    const [fadingOut, setFadingOut] = useState(false);

    const isCompleteRef = useRef(isComplete);
    const onDoneRef = useRef(onDone);

    useEffect(() => { isCompleteRef.current = isComplete; }, [isComplete]);
    useEffect(() => { onDoneRef.current = onDone; }, [onDone]);

    useEffect(() => {
        const timers = [];

        function schedule(fn, delay) {
            const id = setTimeout(fn, delay);
            timers.push(id);
        }

        let litIndices = [];
        let stepsCompleted = 0;

        function lightTiles(stepIdx) {
            const color = TILE_COLORS[stepIdx % TILE_COLORS.length];
            setBlocks(prev => {
                const unlitIdxs = prev
                    .map((b, i) => (b.color ? -1 : i))
                    .filter(i => i >= 0)
                    .sort(() => Math.random() - 0.5)
                    .slice(0, 3);

                let toUnlight = [];
                if (litIndices.length >= 14) {
                    toUnlight = litIndices.splice(0, 3);
                }
                litIndices.push(...unlitIdxs);

                return prev.map((b, i) => {
                    if (toUnlight.includes(i)) return { ...b, color: null };
                    if (unlitIdxs.includes(i)) return { ...b, color };
                    return b;
                });
            });
        }

        function floodTiles() {
            setBlocks(prev => prev.map(b => ({ ...b, color: 'secondary' })));
        }

        function typeText(text, onTyped) {
            let i = 0;
            function tick() {
                i++;
                setCurrentTyped(text.slice(0, i));
                if (i < text.length) {
                    schedule(tick, CHAR_DELAY_MIN + Math.random() * CHAR_DELAY_RANGE);
                } else {
                    onTyped();
                }
            }
            tick();
        }

        function runFinish() {
            setPhase('finishing');
            typeText("Your lesson plan is ready", () => {
                floodTiles();
                schedule(() => {
                    setFadingOut(true);
                    schedule(() => {
                        if (onDoneRef.current) onDoneRef.current();
                    }, FADE_MS);
                }, FINISH_HOLD_MS);
            });
        }

        function runStep(stepIdx) {
            setPhase('stepping');
            setCurrentTyped('');
            lightTiles(stepIdx);

            const text = STEPS[stepIdx % STEPS.length];
            typeText(text, () => {
                stepsCompleted++;
                schedule(() => {
                    const shouldFinish = isCompleteRef.current && stepsCompleted >= MIN_STEPS;
                    setPrevStep({ text });
                    setCurrentTyped('');
                    if (shouldFinish) {
                        runFinish();
                    } else {
                        runStep(stepIdx + 1);
                    }
                }, STEP_HOLD_MS);
            });
        }

        for (let i = 0; i < TILE_COUNT; i++) {
            const idx = i;
            schedule(() => {
                setBlocks(prev => prev.map((b, j) => j === idx ? { ...b, visible: true } : b));
            }, 200 + idx * 30);
        }

        // Start first step once tiles have had a moment to appear (matches prototype timing)
        schedule(() => runStep(0), 900);

        return () => timers.forEach(clearTimeout);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const CheckIcon = () => (
        <div className="ch-lesson-plan-generating-check" aria-hidden="true">
            <svg viewBox="0 0 10 10">
                <polyline points="1.5,5 4,7.5 8.5,2.5" />
            </svg>
        </div>
    );

    return (
        <div className={`ch-lesson-plan-generating${fadingOut ? ' ch-lesson-plan-generating--fading' : ''}`}>
        <div className="ch-lesson-plan-generating-inner">
            <div className="ch-lesson-plan-generating-grid" aria-hidden="true">
                {blocks.map((block, i) => (
                    <div
                        key={i}
                        className={[
                            'ch-lesson-plan-generating-tile',
                            block.visible ? 'ch-lesson-plan-generating-tile--visible' : '',
                            block.color ? `ch-lesson-plan-generating-tile--${block.color}` : '',
                        ].filter(Boolean).join(' ')}
                    />
                ))}
            </div>
            <div className="ch-lesson-plan-generating-steps" aria-live="polite" aria-label="Generation progress">
                {prevStep && (
                    <div className="ch-lesson-plan-generating-step ch-lesson-plan-generating-step--prev">
                        <CheckIcon />
                        <span className="ch-lesson-plan-generating-step-text">{prevStep.text}</span>
                    </div>
                )}
                {phase !== 'starting' && (
                    <div className="ch-lesson-plan-generating-step ch-lesson-plan-generating-step--current">
                        {phase === 'finishing'
                            ? <CheckIcon />
                            : <div className="ch-lesson-plan-generating-dot" aria-hidden="true" />
                        }
                        <span className="ch-lesson-plan-generating-step-text">
                            {currentTyped}
                        </span>
                    </div>
                )}
            </div>
        </div>
        </div>
    );
};

export default LessonPlanGenerating;
